/**
 * 旅行回忆系统 - 空间管理页面
 * 创建、编辑空间，管理成员
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTravelMemories } from '../contexts/TravelMemoriesContext';
import { spaceApi, spaceMemberApi } from '../services/travelMemoriesApi';
import './TravelMemoriesSpaceManage.css';

const TravelMemoriesSpaceManage = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // 编辑模式下的空间ID
  const { currentSpace, refreshSpaces } = useTravelMemories();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    coverImage: ''
  });
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const isEditMode = !!id;

  useEffect(() => {
    if (isEditMode && currentSpace) {
      loadSpaceData();
      loadMembers();
    }
  }, [isEditMode, currentSpace]);

  const loadSpaceData = () => {
    if (currentSpace) {
      setFormData({
        name: currentSpace.name || '',
        description: currentSpace.description || '',
        coverImage: currentSpace.coverImage || ''
      });
    }
  };

  const loadMembers = async () => {
    if (!currentSpace) return;

    try {
      setLoading(true);
      const response = await spaceMemberApi.getMembers(currentSpace._id);
      if (response.data.success) {
        setMembers(response.data.data || []);
      }
    } catch (error) {
      console.error('加载成员列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert('空间名称不能为空');
      return;
    }

    try {
      setSaving(true);
      if (isEditMode) {
        await spaceApi.updateSpace(currentSpace._id, formData);
      } else {
        const response = await spaceApi.createSpace(formData);
        if (response.data.success) {
          await refreshSpaces();
          navigate('/travel-memories');
          return;
        }
      }
      await refreshSpaces();
      navigate('/travel-memories');
    } catch (error) {
      console.error('保存空间失败:', error);
      alert('保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async (userId, role) => {
    if (!currentSpace) return;

    try {
      await spaceMemberApi.addMember(currentSpace._id, { userId, role });
      await loadMembers();
    } catch (error) {
      console.error('添加成员失败:', error);
      alert('添加成员失败，请重试');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!currentSpace) return;
    if (!window.confirm('确定要移除这个成员吗？')) return;

    try {
      await spaceMemberApi.removeMember(currentSpace._id, userId);
      await loadMembers();
    } catch (error) {
      console.error('移除成员失败:', error);
      alert('移除成员失败，请重试');
    }
  };

  const canManageMembers = currentSpace && currentSpace.role === 'owner';

  return (
    <div className="tm-space-manage">
      <div className="tm-space-manage-container">
        <header className="tm-space-manage-header">
          <button className="tm-back-btn" onClick={() => navigate('/travel-memories')}>
            ← 返回
          </button>
          <h1>{isEditMode ? '编辑空间' : '创建空间'}</h1>
        </header>

        <form onSubmit={handleSubmit} className="tm-space-form">
          <div className="tm-form-group">
            <label>空间名称 *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="输入空间名称"
              required
            />
          </div>

          <div className="tm-form-group">
            <label>空间描述</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="输入空间描述"
              rows={4}
            />
          </div>

          <div className="tm-form-group">
            <label>封面图片URL</label>
            <input
              type="url"
              value={formData.coverImage}
              onChange={(e) => setFormData({ ...formData, coverImage: e.target.value })}
              placeholder="https://..."
            />
          </div>

          <div className="tm-form-actions">
            <button type="button" onClick={() => navigate('/travel-memories')}>
              取消
            </button>
            <button type="submit" disabled={saving}>
              {saving ? '保存中...' : '保存'}
            </button>
          </div>
        </form>

        {/* 成员管理（仅编辑模式且为owner） */}
        {isEditMode && canManageMembers && (
          <section className="tm-members-section">
            <h2>空间成员</h2>
            {loading ? (
              <div>加载中...</div>
            ) : (
              <div className="tm-members-list">
                {members.map((member) => (
                  <div key={member.userId} className="tm-member-item">
                    <div className="tm-member-info">
                      <span className="tm-member-name">{member.name || member.username}</span>
                      <span className={`tm-member-role tm-role-${member.role}`}>
                        {member.role === 'owner' ? '所有者' : member.role === 'editor' ? '编辑者' : '查看者'}
                      </span>
                    </div>
                    {member.role !== 'owner' && (
                      <button
                        className="tm-remove-member-btn"
                        onClick={() => handleRemoveMember(member.userId)}
                      >
                        移除
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default TravelMemoriesSpaceManage;
